import { Box, FormControlLabel, Link, Radio, RadioGroup, Typography } from '@mui/material';
import { FiExternalLink } from 'react-icons/fi';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import Checkbox from 'components/Common/Checkbox';
import { VERTICAL_OPTIONS } from './a2pConstants';
import {
  BORDER_COLOR,
  OPT_IN_TEMPLATE_URL,
  OptInMethod,
  USECASE_OPTIONS,
  StepFormProps,
  shrinkLabel
} from './campaignFormShared';

const DISCLOSURES = (brandName?: string) => [
  `I consent to receive SMS text messages from ${brandName || '[Business Name]'} for appointment reminders, account notifications, and general two-way communication.`,
  'Msg frequency varies. Msg & data rates may apply.',
  'Reply HELP for support. Reply STOP to opt out.',
  'See our privacy policy for more information.'
];

const METHODS: Array<{
  id: OptInMethod;
  title: string;
  description: string;
  hint: string;
  detailsTitle: string;
  showUrl?: boolean;
  urlLabel?: string;
  urlPlaceholder?: string;
  showScript?: boolean;
  scriptHelper?: string;
  showPosNote?: boolean;
}> = [
  {
    id: 'website_public',
    title: 'Website Form (Public)',
    description: 'Patients opt in through a live, public web form.',
    hint: 'A valid, reachable link must be submitted as proof.',
    detailsTitle:
      'Your Messaging Opt-In Agreement must allow your patients to give their phone number and express explicit consent (e.g. via checkbox or signature field), while also communicating the following disclosures:',
    showUrl: true,
    urlLabel: 'Opt-in page URL',
    urlPlaceholder: 'https://yoursite.com/sms-sign.up'
  },
  {
    id: 'website_not_public',
    title: 'Website Form (Not Yet Public)',
    description:
      "Patients opt in through a form that isn't public yet (pre-launch, behind login, or a patient portal).",
    hint: 'A public consent-documentation page you publish must be submitted as proof',
    detailsTitle:
      'Your consent page must allow your patients to give their phone number and express explicit consent (e.g. via checkbox or signature field), while also communicating the following disclosures:',
    showUrl: true,
    urlLabel: 'Public URL to your opt-in documentation page',
    urlPlaceholder: 'https://yoursite.com/sms-consent'
  },
  {
    id: 'printed',
    title: 'Printed or Digital Document',
    description: 'Patients opt in through a printed or digital intake form.',
    hint: 'A public page describing this form must be submitted as proof.',
    detailsTitle:
      'Your Messaging Opt-In Agreement must allow your patients to give their phone number and express explicit consent (e.g. via checkbox or signature field), while also communicating the following disclosures:',
    showUrl: true,
    urlLabel: 'Public URL to your intake form documentation page',
    urlPlaceholder: 'https://yoursite.com/sms-consent'
  },
  {
    id: 'verbal',
    title: 'Verbal & Text',
    description: 'Patients opt in verbally (in person or by phone), or by texting a keyword.',
    hint: 'A valid verbal disclosure script must be provided and agreed to.',
    detailsTitle:
      'Your verbal script must allow your patients to give their phone number and express explicit consent (e.g. via checkbox or signature field), while also communicating the following disclosures:',
    showScript: true,
    scriptHelper: 'This script is your proof of consent. No URL or screenshot needed for this method.'
  },
  {
    id: 'pos',
    title: 'Point of Sale',
    description: 'Patients opt in in person at checkout or the front desk.',
    hint: 'A short description of the sign-up process is required; publish a public page if a physical form is used.',
    detailsTitle:
      'Your Messaging Opt-In Agreement must allow your patients to give their phone number and express explicit consent (e.g. via checkbox or signature field), while also communicating the following disclosures:',
    showPosNote: true,
    showUrl: true,
    urlPlaceholder: 'https://yoursite.com/in-person-consent'
  }
];

function OptInDetails({
  method,
  control,
  brandName
}: {
  method: (typeof METHODS)[number];
  control: StepFormProps['control'];
  brandName?: string;
}) {
  return (
    <Box bgcolor='#F8FAFC' borderRadius='8px' p={2} mb={1.5}>
      <Typography fontSize={14} fontWeight={600} color='text.primary' mb={1} lineHeight={1.5}>
        {method.detailsTitle}
      </Typography>
      <Box component='ul' sx={{ m: 0, pl: 2.25, mb: 1.5 }}>
        {DISCLOSURES(brandName).map((item) => (
          <Typography key={item} component='li' fontSize={13} color='text.secondary' lineHeight={1.6} mb={0.5}>
            {item}
          </Typography>
        ))}
      </Box>
      <Link
        href={OPT_IN_TEMPLATE_URL}
        target='_blank'
        rel='noreferrer'
        underline='none'
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 13, mb: 2, fontWeight: 500 }}
      >
        <FiExternalLink size={14} />
        Need Help? See Our Opt-In Template
      </Link>
      {method.showPosNote && (
        <Box mb={1.5}>
          <Input
            name='pos_description'
            control={control}
            label='Public URL to your sign-up documentation'
            placeholder='e.g. Customers fill out a consent card at the front desk and check a box to opt in to SMS reminders.'
            fullWidth
            multiline
            minRows={3}
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Sign-up documentation is required' }}
          />
        </Box>
      )}
      {method.showScript && (
        <Box mb={1}>
          <Input
            name='optin_script'
            control={control}
            label='Verbal opt-in script'
            required
            fullWidth
            multiline
            minRows={3}
            InputLabelProps={shrinkLabel}
            rules={{ required: 'Verbal opt-in script is required' }}
          />
          {method.scriptHelper && (
            <Typography fontSize={12} color='text.secondary' mt={0.75}>
              {method.scriptHelper}
            </Typography>
          )}
        </Box>
      )}
      {method.showUrl && (
        <Input
          name='optin_page_url'
          control={control}
          label={method.urlLabel || ''}
          placeholder={method.urlPlaceholder}
          required={!method.showPosNote}
          fullWidth
          InputLabelProps={shrinkLabel}
          rules={method.showPosNote ? undefined : { required: 'URL is required' }}
        />
      )}
      <Box mt={1.5}>
        <Input
          name='message_flow'
          control={control}
          label='Message Flow'
          required
          fullWidth
          multiline
          minRows={4}
          InputLabelProps={shrinkLabel}
          rules={{ required: 'Message flow is required' }}
        />
      </Box>
      <Box mt={0.5}>
        <Checkbox
          name='optin_verified'
          control={control}
          label='I verify my Messaging Opt-In Agreement contains the disclosures above and allows my patients to enter their phone number and grant consent.'
        />
      </Box>
    </Box>
  );
}

export default function ConsentDetailsStep({ control, watch, setValue, lockType, brandName }: StepFormProps) {
  const method = watch('optin_method') || 'website_public';

  return (
    <Box>
      <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={2.5} alignItems='start'>
        <Select
          name='usecases'
          control={control}
          label='Use Cases'
          options={USECASE_OPTIONS}
          multiple
          fullWidth
          disabled={lockType}
          sortOrder='none'
          rules={{
            validate: (value: string[]) => (Array.isArray(value) && value.length >= 2) || 'Select at least 2 use cases'
          }}
        />
        <Select
          name='vertical'
          control={control}
          label='Vertical Type'
          options={VERTICAL_OPTIONS}
          fullWidth
          required
          disabled={lockType}
          displayEmpty
          sortOrder='none'
          helperText='The industry your business operates in'
          rules={{ required: 'Vertical type is required' }}
        />
      </Box>

      <Input
        name='description'
        control={control}
        label='Campaign Description'
        required
        fullWidth
        multiline
        minRows={6}
        InputLabelProps={shrinkLabel}
        helperText='A clear explanation of what this campaign does, who it messages, and why. This is the first thing reviewers read.'
        rules={{ required: 'Campaign description is required' }}
      />

      <Typography fontSize={16} fontWeight={600} mt={3} mb={1.5}>
        Messaging Opt-In Agreement
      </Typography>
      <Typography fontSize={14} fontWeight={500} mb={1.5}>
        How do your patients primarily provide messaging consent?
      </Typography>

      <RadioGroup
        value={method}
        onChange={(_, value) => setValue('optin_method', value as OptInMethod, { shouldDirty: true })}
      >
        {METHODS.map((item) => {
          const selected = method === item.id;
          return (
            <Box key={item.id}>
              <Box
                onClick={() => setValue('optin_method', item.id, { shouldDirty: true })}
                border={`1px solid ${selected ? '#93C5FD' : BORDER_COLOR}`}
                bgcolor={selected ? '#F8FBFF' : '#fff'}
                borderRadius='8px'
                px={2}
                py={1.5}
                mb={selected ? 1.5 : 1.25}
                sx={{ cursor: 'pointer' }}
              >
                <FormControlLabel
                  value={item.id}
                  control={<Radio size='small' />}
                  label={
                    <Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {item.title}
                      </Typography>
                      <Typography fontSize={13} color='text.secondary' mt={0.25}>
                        {item.description}
                      </Typography>
                      <Typography fontSize={12} color='text.secondary' fontStyle='italic' mt={0.25}>
                        {item.hint}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', m: 0, '& .MuiRadio-root': { pt: 0.25 } }}
                />
              </Box>
              {selected && <OptInDetails method={item} control={control} brandName={brandName} />}
            </Box>
          );
        })}
      </RadioGroup>
    </Box>
  );
}
